import React, { useEffect, useState } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { IdentityType, TokenType } from '@vatom/sdk/core'
import { useIdentities } from '@vatom/sdk/react'
import { translate } from '@vatom/utils'
import { theme } from '@vatom/wombo'
import { Actionsheet, KeyboardAvoidingView, VStack } from 'native-base'

import Alert from '../../../components/Alert'
import { useMaxWith } from '../../../components/WebWrapper'
import { AppRoutes } from '../../../navigators'
import ActionSheetHeader, { HeaderType } from '../partials/ActionSheetHeader'

import CustomAddress from './partials/CustomAddress'
import MyAddress from './partials/MyAddress'

interface ActionSheetMintProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  token: TokenType
}

export enum State {
  myAddress = 'myAddress',
  customAddress = 'customAddress',
  transactionSent = 'transactionSent'
}

export function getIdentityTypeFromNetwork(network?: string): IdentityType {
  switch (network) {
    case 'solana':
      return IdentityType.Solana
    case 'casper_testnet':
    case 'casper_mainnet':
      return IdentityType.Casper
    default:
      return IdentityType.Eth
  }
}

export const ActionSheetMint = ({ isOpen, setOpen, token }: ActionSheetMintProps) => {
  const navigation = useNavigation()
  const { height: screenHeight } = useWindowDimensions()
  const maxWidth = useMaxWith()
  const insets = useSafeAreaInsets()
  const [state, setState] = useState<State>(State.myAddress)
  const identityType = getIdentityTypeFromNetwork(token.blockchainInfo?.network)
  const { allowedIdentities } = useIdentities({ allowedTypes: [identityType] })
  const [selectedAddress, setSelectedAddress] = useState('')
  const [mintAddress, setMintAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setMintAddress(selectedAddress)
  }, [selectedAddress])

  if (!isOpen) {
    return null
  }

  const onMint = async () => {
    if (!token.blockchainInfo?.network) {
      console.error('No network found')
      return
    }
    const netWork = token.blockchainInfo.network
    setIsLoading(true)

    if (mintAddress) {
      await token
        .performAction('MintNFT', {
          'new.owner.address': mintAddress
        })
        .then(res => {
          setOpen(false)
          navigation.navigate(AppRoutes.MintSuccess, {
            transactionId: res?.tx_hash,
            network: netWork,
            address: mintAddress
          })
        })
        .catch(Alert.showError)
    }

    setIsLoading(false)
  }

  const onClose = () => {
    setOpen(false)
    navigation.navigate(AppRoutes.home)
  }

  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      margin={'auto'}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      <KeyboardAvoidingView w="100%" behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <Actionsheet.Content
          bg="white"
          pt="20px"
          pb="16px"
          px={0}
          _light={{
            backgroundColor: theme.colors.white
          }}
          _dark={{
            backgroundColor: theme.colors.grayDarkMode[900]
          }}
          maxHeight={screenHeight - insets.top}
          height={screenHeight - insets.top}
        >
          <VStack w="100%" flex={1} px="16px">
            <ActionSheetHeader
              type={HeaderType.close}
              hasLearnMore={true}
              onPress={() => {
                state === State.customAddress ? setState(State.myAddress) : onClose()
                setState(State.myAddress)
              }}
              title={
                state === State.myAddress
                  ? translate('actionSheets.mintOn', {
                      blockchain: token?.blockchainInfo?.networkName
                    })
                  : ''
              }
              hasText={state === State.transactionSent ? false : true}
              text={state === State.customAddress ? translate('mintNft.provideAddress') : ''}
            />
            {state !== State.transactionSent && state === State.myAddress && (
              <MyAddress
                isLoading={isLoading}
                identities={allowedIdentities}
                onMint={onMint}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                onSetState={() => {
                  if (!isLoading) {
                    setState(State.customAddress)
                    setSelectedAddress('')
                  }
                }}
              />
            )}
            {state !== State.transactionSent && state === State.customAddress && (
              <CustomAddress
                isLoading={isLoading}
                onMint={onMint}
                mintAddress={mintAddress}
                setMintAddress={setMintAddress}
                onSetState={() => {
                  if (!isLoading) {
                    setState(State.myAddress)
                  }
                }}
              />
            )}
          </VStack>
        </Actionsheet.Content>
      </KeyboardAvoidingView>
    </Actionsheet>
  )
}
export default ActionSheetMint
